/////React/////
import React from 'react';
////Material UI/////
import {Box, LinearProgress, Grid, useTheme} from '@mui/material';
/////React Router Dom/////
import {useNavigate} from 'react-router-dom';
/////Local Shared Types & Images/////
import {
  LogoSize,
  IProcess,
  DefaultIProcess,
  ProcessTabs,
  Custom_Encode_URL,
  E_QuickLinks,
  BannerType,
  LogInType,
} from 'app/types/Shared';
import smallLogo from 'assets/images/header/elevateLogoSmall.svg';
import largeLogo from 'assets/images/header/ElevateLogoLarge.svg';
import takedaLogo from 'assets/images/header/takedaLogo.svg';
import OneYear from 'assets/images/1 Year.png';
/////Local Components/////
import PageLayout from 'components/PageLayout/PageLayout';
import TopNavigation from 'components/TopNavigation/TopNavigation';

import Home from 'components/ProcessNavigation/Home';
/////Store/////
import {useDispatch, useSelector} from 'react-redux';
import {
  setSelectedProcess,
  selectOnboarding,
  setOnboarding,
  setProcessPageTab,
  setProcessPageTabIndex,
  OnboardingActions,
  setSimplifiedRACI,
  setSelectedL2Label,
  setSelectedL1Label,
  setCurrentMapXL,
  selectSelectedGroup,
  setSelectedGroup,
  setOneYearBannerOpen,
  selectOneYearBannerOpen,
  resetInactivityLogout,
  selectInactivityLogout,
} from 'app/store/appState/appStateSlice';
import {selectAllProcesses, selectAllPhases} from 'app/store/operatingModel/operatingModelSlice';
import {useMatomo} from '@jonkoops/matomo-tracker-react';
import ChangeRole from 'components/ChangeRole/ChangeRole';
import {useFeatureFlag} from 'configcat-react';
import WelcomeScreen from 'components/Welcome/Welcome';
import Banner from 'components/Banner/Banner';
import {selectUserType} from 'app/store/users/userSlice';

const HomePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const onboarding = useSelector(selectOnboarding);
  const allProcesses = useSelector(selectAllProcesses);
  const allPhases = useSelector(selectAllPhases);
  const selectedGroup = useSelector(selectSelectedGroup);
  const oneYearBannerOpen = useSelector(selectOneYearBannerOpen);
  const userType = useSelector(selectUserType);
  const {value: isChangeNotificationsEnabled} = useFeatureFlag('feature_change_notifications', false);
  const {value: isOneYearBannerEnabled} = useFeatureFlag('content_one_year_banner', false);
  const inactivityLogoutState = useSelector(selectInactivityLogout);
  const hasRunOnce = React.useRef(false);

  const getLastShownDate = (): string | null => {
    return localStorage.getItem('popupLastShownTime');
  };

  const setLastShownDate = (date: string): void => {
    localStorage.setItem('popupLastShownTime', date);
  };

  const dispatch = useDispatch();

  React.useEffect(() => {
    const today = new Date().toISOString().split('T')[0]; // Get YYYY-MM-DD
    const lastShownDate = getLastShownDate();
    if (lastShownDate !== today && userType === LogInType.Internal) {
      dispatch(setOneYearBannerOpen(true));
      setLastShownDate(today);
    }
  }, [dispatch, userType]);

  // Matomo Analytics
  const {trackPageView, trackEvent} = useMatomo();
  React.useEffect(() => {
    trackPageView();
  });

  React.useEffect(() => {
    if (hasRunOnce.current) return;
    hasRunOnce.current = true;

    if (!inactivityLogoutState) {
      dispatch(setProcessPageTab(ProcessTabs.Map));
      dispatch(setSimplifiedRACI(false));
      dispatch(setProcessPageTabIndex(isChangeNotificationsEnabled ? 1 : 0));
      dispatch(setSelectedL1Label(''));
      dispatch(setSelectedL2Label(''));
      dispatch(setCurrentMapXL(false));
      dispatch(setSelectedProcess(DefaultIProcess));
    } else {
      dispatch(resetInactivityLogout());
    }
  }, [dispatch, isChangeNotificationsEnabled, inactivityLogoutState]);

  React.useEffect(() => {
    const inQuickLinks = (group: string) => {
      if (group === E_QuickLinks.TDOM) {
        return true;
      } else if (group === E_QuickLinks.Updates) {
        return isChangeNotificationsEnabled;
      } else return false;
    };

    const groups = allProcesses?.reduce((acc, curr) => {
      if (!acc.includes(curr.category_group)) {
        acc.push(curr.category_group);
      }
      return acc;
    }, [] as Array<string>);
    if (!selectedGroup || (!groups?.includes(selectedGroup) && !inQuickLinks(selectedGroup) && groups)) {
      console.log('Setting selected group to first group');
      console.log(`Selected Group :: ${selectedGroup}`);
      const firstGroup = groups?.at(0);
      if (firstGroup) {
        dispatch(setSelectedGroup(firstGroup));
      }
    }
  }, [selectedGroup, allProcesses, dispatch, isChangeNotificationsEnabled]);

  const handleProcessClick = (process: IProcess) => {
    trackEvent({category: 'Process', action: 'Process Selected', name: process.label});

    dispatch(setProcessPageTabIndex(isChangeNotificationsEnabled ? 1 : 0));
    dispatch(setSelectedProcess(process));
    const url = '/process/' + Custom_Encode_URL(process?.name) + '/' + process?.operating_models_list;
    navigate(url);
  };

  const onLogout = async () => {
    dispatch(setOnboarding(OnboardingActions.EnableAll));
    navigate('/logout');
    trackEvent({category: 'Navigation', action: 'Log Out'});
    return Promise.resolve();
  };

  const toResources = async () => {
    navigate('/resources');
    return Promise.resolve();
  };

  const onSearch = (query?: string) => {
    navigate(`/search?q=${query}`);
    return Promise.resolve();
  };

  // Header scroll & start at top of page
  const [onFirstLoad, setFirstLoad] = React.useState(true);
  React.useEffect(() => {
    if (onFirstLoad) {
      window.scrollTo(0, 0);
      setFirstLoad(false);
    }
  }, [onFirstLoad]);

  const header = (
    <TopNavigation
      authorized={true}
      logout={onLogout}
      search={onSearch}
      smallLogo={smallLogo}
      largeLogo={largeLogo}
      rightLogo={takedaLogo}
      LogoType={LogoSize.Small}
      toResources={toResources}
      banner={onboarding ? onboarding.topBanner : false}
    />
  );
  const footer = <></>;

  const renderOnboarding = () => {
    return onboarding && onboarding.onboardingModal;
  };

  return (
    <>
      {!allProcesses || !allPhases ? (
        <Box sx={{pt: 10, width: '100%'}}>
          <LinearProgress color='secondary' />
        </Box>
      ) : (
        <>
          <PageLayout header={header} footer={footer} globalBannerCallback={onLogout}>
            {renderOnboarding() ? (
              <WelcomeScreen />
            ) : (
              <Box
                width='100%'
                display='flex'
                alignItems='flex-start'
                justifyContent='center'
                marginTop={onboarding?.topBanner ? 7.5 : 2.5}
              >
                <Grid container rowGap='10px'>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box maxWidth={1500} width='100%' display='flex' justifyContent='flex-end' height='70px'>
                      <ChangeRole />
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      bgcolor: theme.palette.grey[100],
                      borderTop: '1px solid #D9D9D9',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'flex-start',
                      minHeight: '82.5vh',
                    }}
                  >
                    <Box maxWidth={1500} width='100%'>
                      <Home processClickCallback={handleProcessClick} />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
            {!renderOnboarding() && isOneYearBannerEnabled && userType === LogInType.Internal && (
              <Banner
                type={BannerType.Page}
                open={oneYearBannerOpen}
                title='Happy birthday, ELEVATE!'
                subtitle='We couldn’t have done it without you. Help us celebrate by joining our Scavenger Hunt and you could win an ELEVATE t-shirt!'
                image={OneYear}
                button='Let the Hunt begin!'
                height={550}
                width={800}
                onClose={() => dispatch(setOneYearBannerOpen(false))}
                onButtonClick={() => {
                  window.open('https://forms.office.com/e/TF1eZzLUgm', '_blank');
                  dispatch(setOneYearBannerOpen(false));
                }}
              />
            )}
          </PageLayout>
        </>
      )}
    </>
  );
};

export default HomePage;
